import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/searchable-select";
import { AlertCircle, Download, ExternalLink } from "lucide-react";
import { searchIncludes } from "@/lib/search-normalize";
import {
  SKIP_REASON_LABELS,
  formatDateTime,
  formatMoney,
  type CollectionActivityResponse,
  type CollectionPropertiesResponse,
} from "./_shared";

// Activity Log (Task #4830). The old version listed only sent notices with no
// filters and no property column. This shows every decision the engine made —
// including skips and why — filterable by property and member.

const PAGE_SIZE = 50;

export function ActivityLogTab() {
  const [propertyId, setPropertyId] = useState("all");
  const [memberQuery, setMemberQuery] = useState("");
  const [outcome, setOutcome] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);

  const propertiesQuery = useQuery<CollectionPropertiesResponse>({
    queryKey: ["/api/app/collections/properties"],
  });

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (propertyId !== "all") params.set("propertyId", propertyId);
    if (outcome !== "all") params.set("outcome", outcome);
    if (from) params.set("from", new Date(from).toISOString());
    // Include the whole end day rather than midnight-exactly, which would drop
    // everything logged on the selected date.
    if (to) params.set("to", new Date(`${to}T23:59:59.999`).toISOString());
    params.set("page", String(page));
    params.set("limit", String(PAGE_SIZE));
    return params.toString();
  }, [propertyId, outcome, from, to, page]);

  const activityQuery = useQuery<CollectionActivityResponse>({
    queryKey: [`/api/app/collections/activity?${queryString}`],
  });

  const allRows = activityQuery.data?.rows ?? [];
  // Member filtering is client-side on the current page: the server filter
  // takes a memberId, but operators search by name.
  const rows = memberQuery.trim()
    ? allRows.filter((r) => searchIncludes(r.memberName, memberQuery))
    : allRows;
  const total = activityQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const resetPage = <T,>(setter: (v: T) => void) => (v: T) => { setter(v); setPage(1); };

  return (
    <div data-testid="collections-activity-log">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Activity Log</h2>
          <p className="mt-1 text-sm text-gray-500">
            Every member the engine evaluated — messages sent, and anyone skipped along with the reason.
          </p>
        </div>
        <Button variant="outline" asChild className="flex items-center gap-2" data-testid="button-export-activity">
          <a href={`/api/app/collections/activity/export.csv?${queryString}`}>
            <Download className="h-4 w-4" />
            Export CSV
          </a>
        </Button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <SearchableSelect
          value={propertyId}
          onValueChange={resetPage(setPropertyId)}
          placeholder="All properties"
          searchPlaceholder="Search properties..."
          emptyText="No properties match."
          testId="select-activity-property"
          className="h-9 w-[200px]"
          options={[
            { value: "all", label: "All properties" },
            ...(propertiesQuery.data?.properties ?? []).map((p) => ({
              value: String(p.propertyId),
              label: p.name,
              keywords: p.address ? `${p.name} ${p.address}` : undefined,
            })),
          ]}
        />

        <Input
          placeholder="Filter by member..."
          value={memberQuery}
          onChange={(e) => setMemberQuery(e.target.value)}
          className="h-9 w-48"
          data-testid="input-activity-member"
        />

        <Select value={outcome} onValueChange={resetPage(setOutcome)}>
          <SelectTrigger className="h-9 w-[150px]" data-testid="select-activity-outcome">
            <SelectValue placeholder="All outcomes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All outcomes</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="skipped">Skipped</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>

        <Input type="date" value={from} onChange={(e) => resetPage(setFrom)(e.target.value)} className="h-9 w-40" data-testid="input-activity-from" />
        <Input type="date" value={to} onChange={(e) => resetPage(setTo)(e.target.value)} className="h-9 w-40" data-testid="input-activity-to" />
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200" data-tour="collections-list">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="activity-table">
            <thead className="border-b border-gray-200 bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Property</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Member</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Outcome</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Message</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {activityQuery.isLoading ? (
                <tr><td colSpan={7} className="px-4 py-6"><Skeleton className="h-6 w-full" /></td></tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <AlertCircle className="mx-auto mb-2 h-8 w-8 text-gray-300" />
                    <p className="text-sm text-gray-400">No collections activity yet.</p>
                    <p className="mt-1 text-xs text-gray-400">
                      Activity appears here after collections runs for an enabled property.
                    </p>
                  </td>
                </tr>
              ) : rows.map((row) => (
                <tr key={row.id} data-testid={`row-activity-${row.id}`}>
                  <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-500" data-testid={`text-activity-date-${row.id}`}>
                    {formatDateTime(row.createdAt)}
                  </td>
                  <td className="px-4 py-3 text-gray-700" data-testid={`text-activity-property-${row.id}`}>
                    {row.resolvedPropertyName ?? "—"}
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-900" data-testid={`text-activity-member-${row.id}`}>
                    {row.memberName}
                  </td>
                  <td className="px-4 py-3 font-medium text-red-600" data-testid={`text-activity-amount-${row.id}`}>
                    {formatMoney(row.amountDue)}
                  </td>
                  <td className="px-4 py-3" data-testid={`text-activity-outcome-${row.id}`}>
                    {row.outcome === "sent" ? (
                      <Badge>Sent</Badge>
                    ) : row.outcome === "failed" ? (
                      <Badge variant="destructive">Failed</Badge>
                    ) : (
                      <Badge variant="outline">
                        {SKIP_REASON_LABELS[row.skipReason ?? ""] ?? "Skipped"}
                      </Badge>
                    )}
                  </td>
                  <td className="max-w-xs px-4 py-3 text-gray-600">
                    <span className="line-clamp-2 text-xs">{row.messageContent ?? "—"}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {row.memberId != null && (
                      <Link
                        href={`/app/messages?member=${row.memberId}`}
                        className="inline-flex items-center gap-1 text-xs text-brand hover:underline"
                        data-testid={`link-activity-thread-${row.id}`}
                      >
                        View thread <ExternalLink className="h-3 w-3" />
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <span className="text-sm text-gray-500" data-testid="text-activity-pagination">
            Page {page} of {totalPages} · {total} entries
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              data-testid="button-activity-prev"
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              data-testid="button-activity-next"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
